/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { AlertTriangle, ArrowRight, ChevronDown, Globe, Pencil, Server, UserRound, UserSquare2, Users, Wallet } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useSearchParams } from '@/lib/scopedParams';
import { api, isApiError } from '../../api';
import type { CustomerBilling, CustomerOverview, CustomerProfileSummary } from '../../api/areas/accounts';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { NavTabs } from '../../components/NavTabs';
import { CustomerScope } from '../../components/NotifyCustomer';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate, formatDateTime, formatMoney, formatRelative } from '../../lib/format';
import { useAccess } from '../../lib/access';
import { useApi, type UseApiResult } from '../../lib/useApi';
import { PERMS } from '../../nav/hubs';
import { RegistryRef } from '../domains/refs';
import { RegisterDomainButton } from '../operator/RegisterDomain';
import { NewServiceOrderButton } from '../subscriptions/NewServiceOrder';
import type { Subscription } from '../../api/areas/subscriptions';
import { STATE_TONE, vnd } from '../../lib/subscriptions';
import { ReadinessPanel } from '../subjects/ReadinessPanel';
import { SubjectFormDialog } from '../subjects/SubjectForm';
import { useWrite, WriteError } from '../../components/Write';
import { AddCustomerUserAction, EditCustomerAction } from './CustomerActions';
import { ContactSharingSection, DisclosureSection } from './CustomerReporting';
import { EmailSettingsPanel, TemplatesPanel } from '../notify';
import { WebhooksPanel } from '../work';
import { WalletAdjustAction } from './WalletAdjust';
import { TopUpQrAction } from '../commerce/PaymentQr';
import { LIFECYCLE, StatusBadge } from '../domains/status';
import {
  cellLink,
  CUSTOMER_STATUS,
  DOSSIER_STATUS,
  EnumBadge,
  EnumText,
  Masked,
  MoneyText,
  mutedSmall,
  paths,
  Stack,
  SUBJECT_STATUS,
  TierText,
  useTiers,
  VERIFICATION,
} from './shared';
import { ACCOUNT_TONES, DOC_TONES, ORDER_TONES, OrderRef, PAYMENT_TONES } from './billing';

type User = CustomerOverview['users'][number];
type Subject = CustomerOverview['subjects'][number];
type Domain = CustomerOverview['domains'][number];
type Order = CustomerBilling['orders'][number];
type Invoice = CustomerBilling['invoices'][number];
type Payment = CustomerBilling['payments'][number];
type WalletRow = CustomerBilling['wallets'][number];

function BillingTab({ billing, customer }: { billing: UseApiResult<CustomerBilling>; customer: CustomerOverview['customer'] }) {
  const { t } = useTranslation('accounts');
  if (billing.status === 'loading') return <Loading label={t('customer.billing.loading')} rows={3} />;
  if (billing.status === 'error') {
    if (isApiError(billing.error) && billing.error.status === 403) return <Callout tone="neutral">{t('common.noPermission')}</Callout>;
    return <DataError error={billing.error} onRetry={billing.reload} title={t('customer.billing.loadError')} />;
  }
  const b = billing.data;
  const walletColumns: Column<WalletRow>[] = [
    { key: 'wallet', header: t('customer.billing.walletCol.wallet'), cell: (w) => <EntityRef to={paths.ledgerAccount(w.id)} id={w.id} label={t('customer.billing.wallet', { currency: w.currency })} /> },
    { key: 'status', header: t('customer.billing.walletCol.status'), cell: (w) => <EnumBadge ns="commerce" group="accountStatus" tones={ACCOUNT_TONES} value={w.status} /> },
    { key: 'balance', header: t('customer.billing.walletCol.balance'), numeric: true, cell: (w) => <MoneyText amount={w.balance} currency={w.currency} /> },
    { key: 'held', header: t('customer.billing.walletCol.held'), numeric: true, cell: (w) => <MoneyText amount={w.held} currency={w.currency} /> },
    { key: 'available', header: t('customer.billing.walletCol.available'), numeric: true, cell: (w) => <MoneyText amount={w.available} currency={w.currency} className="font-medium" /> },
  ];
  const orderColumns: Column<Order>[] = [
    // The order's name carries its placing date, so there is no separate date column.
    { key: 'order', header: t('customer.billing.orderCol.order'), cell: (o) => <OrderRef id={o.id} order={o} /> },
    { key: 'status', header: t('customer.billing.orderCol.status'), cell: (o) => <EnumBadge ns="commerce" group="orderStatus" tones={ORDER_TONES} value={o.status} /> },
    { key: 'items', header: t('customer.billing.orderCol.items'), numeric: true, cell: (o) => formatCount(o.item_count) },
    { key: 'total', header: t('customer.billing.orderCol.total'), numeric: true, cell: (o) => <MoneyText amount={o.total} currency={o.currency} /> },
  ];
  const invoiceColumns: Column<Invoice>[] = [
    {
      key: 'number',
      header: t('customer.billing.invoiceCol.number'),
      cell: (i) => (
        <Link to={paths.invoice(i.id)} className={`${cellLink} font-mono text-xs whitespace-nowrap`}>
          {`${i.series}-${i.number}`}
        </Link>
      ),
    },
    { key: 'type', header: t('customer.billing.invoiceCol.type'), cell: (i) => <EnumBadge ns="commerce" group="docType" tones={DOC_TONES} value={i.document_type} /> },
    { key: 'total', header: t('customer.billing.invoiceCol.total'), numeric: true, cell: (i) => <MoneyText amount={i.total} currency={i.currency} /> },
    {
      key: 'order',
      header: t('customer.billing.invoiceCol.order'),
      cell: (i) =>
        i.order_id ? (
          <OrderRef id={i.order_id} order={{ first_domain: i.order_first_domain, item_count: i.order_item_count, created_at: i.order_created_at }} />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { key: 'issued', header: t('customer.billing.invoiceCol.issued'), className: 'whitespace-nowrap', cell: (i) => formatDate(i.issued_at) },
  ];
  const paymentColumns: Column<Payment>[] = [
    {
      key: 'payment',
      header: t('customer.billing.paymentCol.payment'),
      cell: (p) => (
        <Link to={paths.payment(p.id)} className={cellLink}>
          <MoneyText amount={p.amount} currency={p.currency} />
        </Link>
      ),
    },
    { key: 'purpose', header: t('customer.billing.paymentCol.purpose'), cell: (p) => <EnumText ns="commerce" group="paymentPurpose" value={p.purpose} /> },
    { key: 'provider', header: t('customer.billing.paymentCol.provider'), cell: (p) => <span className="font-mono text-xs">{p.provider}</span> },
    { key: 'status', header: t('customer.billing.paymentCol.status'), cell: (p) => <EnumBadge ns="commerce" group="paymentStatus" tones={PAYMENT_TONES} value={p.status} /> },
    { key: 'created', header: t('customer.billing.paymentCol.created'), className: 'whitespace-nowrap', cell: (p) => <span title={formatDateTime(p.created_at)}>{formatRelative(p.created_at)}</span> },
  ];
  return (
    <Stack>
      <Section
        flush
        title={t('customer.billing.wallets')}
        actions={
          <>
            {customer.billing_currency === 'VND' ? <TopUpQrAction customer={{ id: customer.id, name: customer.display_name }} onChanged={billing.reload} /> : null}
            <WalletAdjustAction
              customer={{ id: customer.id, name: customer.display_name, currency: customer.billing_currency }}
              wallet={b.wallets.find((w) => w.currency === customer.billing_currency)}
              onChanged={billing.reload}
            />
          </>
        }
      >
        <DataTable columns={walletColumns} rows={b.wallets} rowKey={(w) => w.id} empty={<EmptyState title={t('customer.billing.noWallets')} />} />
      </Section>
      {b.invoiced.length ? (
        <StatGrid>
          {b.invoiced.map((x) => (
            <StatCard key={x.currency} label={`${t('customer.billing.invoiced')} · ${x.currency}`} value={<MoneyText amount={x.total} currency={x.currency} className="whitespace-normal" />} hint={t('customer.billing.invoices', { count: x.count })} />
          ))}
        </StatGrid>
      ) : null}
      <Section
        flush
        title={t('customer.billing.orders', { count: b.order_count })}
        description={b.order_count > b.orders.length ? t('common.newestN', { count: b.orders.length }) : undefined}
        actions={
          b.order_count > b.orders.length ? (
            <Link className={`${cellLink} text-sm`} to={`/orders?customer_id=${encodeURIComponent(customer.id)}`}>
              {t('customer.billing.viewAll')}
            </Link>
          ) : null
        }
      >
        <DataTable columns={orderColumns} rows={b.orders} rowKey={(o) => o.id} empty={<EmptyState title={t('customer.billing.noOrders')} />} />
      </Section>
      <Section flush title={t('customer.billing.invoices', { count: b.invoice_count })} description={b.invoice_count > b.invoices.length ? t('common.newestN', { count: b.invoices.length }) : undefined}>
        <DataTable columns={invoiceColumns} rows={b.invoices} rowKey={(i) => i.id} empty={<EmptyState title={t('customer.billing.noInvoices')} />} />
      </Section>
      <Section flush title={t('customer.billing.payments', { count: b.payment_count })} description={b.payment_count > b.payments.length ? t('common.newestN', { count: b.payments.length }) : undefined}>
        <DataTable columns={paymentColumns} rows={b.payments} rowKey={(p) => p.id} empty={<EmptyState title={t('customer.billing.noPayments')} />} />
      </Section>
    </Stack>
  );
}

/** The wallet stat: available balance in the billing currency (first wallet otherwise). */
function WalletStat({ billing, currency }: { billing: UseApiResult<CustomerBilling>; currency: string }) {
  const { t } = useTranslation('accounts');
  if (billing.status !== 'success') return <StatCard icon={Wallet} label={t('customer.stat.wallet')} value="—" hint={billing.status === 'error' ? t('common.noPermission') : undefined} />;
  const w = billing.data.wallets.find((x) => x.currency === currency) ?? billing.data.wallets[0];
  if (!w) return <StatCard icon={Wallet} label={t('customer.stat.wallet')} value="—" hint={t('customer.stat.noWallet')} />;
  return (
    <StatCard
      icon={Wallet}
      label={t('customer.stat.wallet')}
      value={<MoneyText amount={w.available} currency={w.currency} className="whitespace-normal" />}
      hint={t('customer.stat.walletHint', { held: formatMoney({ amount: w.held, currency: w.currency }) })}
      to={paths.ledgerAccount(w.id)}
      linkLabel={t('customer.stat.openWallet')}
    />
  );
}

/** The account's billing profile: notification language and the invoice buyer (identity data
 * are the primary subject's). */
function ProfileSection({ p }: { p: CustomerProfileSummary }) {
  const { t } = useTranslation('accounts');
  const dash = (v: string | null | undefined) => (v ? v : '—');
  const rows: [string, ReactNode][] = [
    [t('customer.profile.language'), p.notification_language ? t(`customerForm.languages.${p.notification_language}`) : t('customer.profile.languageByCountry')],
  ];
  const invoice: [string, ReactNode][] = [
    [t('customer.profile.invoiceSource'), p.invoice_same_as_account ? t('customer.profile.invoiceSame') : t('customer.profile.invoiceOwn')],
    ...(p.invoice_same_as_account
      ? []
      : ([
          [t('customerForm.invoiceBuyer'), dash(p.invoice_buyer_name)],
          [t('customerForm.invoiceTaxCode'), p.invoice_tax_code ? <span key="itc" className="font-mono">{p.invoice_tax_code}</span> : '—'],
          [t('customerForm.invoiceAddress'), dash(p.invoice_address)],
          [t('customerForm.invoiceEmails'), p.invoice_email_hints.length ? p.invoice_email_hints.map((e) => <Masked key={e} value={e} />) : '—'],
        ] as [string, ReactNode][])),
  ];
  return (
    <Section title={t('customer.profile.title')}>
      <div className="space-y-4">
        <KeyValue columns={3} rows={rows} />
        <h3 className="text-sm font-medium">{t('customerForm.section.invoice')}</h3>
        <KeyValue columns={3} rows={invoice} />
      </div>
    </Section>
  );
}

/** Links one of the customer's subjects as its primary subject (with a reason). */
function PickPrimary({ customerId, subjects, onDone }: { customerId: string; subjects: Subject[]; onDone: () => void }) {
  const { t } = useTranslation('accounts');
  const [subject, setSubject] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { subject_id: string; reason: string }, opts) => api.setPrimarySubject(customerId, b, opts));
  const eligible = subjects.filter((x) => x.status === 'ACTIVE');
  if (!eligible.length) return null;
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ subject_id: subject, reason: reason.trim() }).then((r) => r && onDone());
      }}
    >
      <label className="grid gap-1 text-xs text-muted-foreground">
        {t('customer.primary.pick')}
        <select className="h-8 rounded-md border bg-transparent px-2 text-sm text-foreground" value={subject} onChange={(e) => setSubject(e.target.value)}>
          <option value="">{t('subjectForm.choose')}</option>
          {eligible.map((x) => (
            <option key={x.id} value={x.id}>
              {x.legal_name ?? x.id}
            </option>
          ))}
        </select>
      </label>
      <label className="grid gap-1 text-xs text-muted-foreground">
        {t('customer.primary.reason')}
        <input className="h-8 rounded-md border bg-transparent px-2 text-sm text-foreground" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <Button type="submit" size="sm" variant="outline" disabled={!subject || !reason.trim() || w.busy}>
        {t('customer.primary.link')}
      </Button>
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
    </form>
  );
}

/**
 * The account holder: the primary subject's name, type and verification, what it still lacks to
 * register domains (the same panel as the subject page), and "Sửa chủ thể" — identity data are
 * edited there (a new version of the subject), never on the account. Without one: "chưa có chủ
 * thể chính" with the actions to link or create it.
 */
function PrimarySubjectCard({ data, onChanged }: { data: CustomerOverview; onChanged: () => void }) {
  const { t } = useTranslation('accounts');
  const [creating, setCreating] = useState(false);
  const c = data.customer;
  const p = data.primary_subject ?? null;
  const link = useWrite((b: { subject_id: string; reason: string }, opts) => api.setPrimarySubject(c.id, b, opts));
  if (!p) {
    return (
      <Section title={t('customer.primary.title')}>
        <div className="grid gap-3" data-primary-subject="none">
          <Callout tone="warn" title={t('customer.primary.none')}>
            {t('customer.primary.noneHint')}
          </Callout>
          <div className="flex flex-wrap items-end gap-3">
            <Button type="button" size="sm" onClick={() => setCreating(true)}>
              <UserSquare2 /> {t('customer.primary.create')}
            </Button>
            <PickPrimary customerId={c.id} subjects={data.subjects} onDone={onChanged} />
          </div>
          {link.state.phase === 'error' ? <WriteError error={link.state.error} /> : null}
          <SubjectFormDialog
            open={creating}
            onOpenChange={setCreating}
            customerId={c.id}
            title={t('customer.primary.createTitle')}
            initial={{ kind: c.account_type === 'ORGANIZATION' ? 'ORGANIZATION' : 'INDIVIDUAL', email: '', legalName: c.account_type === 'ORGANIZATION' ? c.display_name : '' }}
            onSaved={(id) => void link.run({ subject_id: id, reason: t('customer.primary.createdReason') }).then(() => onChanged())}
          />
        </div>
      </Section>
    );
  }
  return (
    <Section
      title={t('customer.primary.title')}
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to={`${paths.subject(p.id)}?edit=1`}>
            <Pencil /> {t('customer.primary.edit')}
          </Link>
        </Button>
      }
    >
      <div className="grid gap-3" data-primary-subject={p.id}>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <EntityRef to={paths.subject(p.id)} id={p.id} label={p.legal_name ?? t('common.hiddenName')} />
          <EnumText group="subjectType" value={p.subject_type} />
          {p.latest_verification_status ? (
            <EnumBadge group="verification" tones={VERIFICATION} value={p.latest_verification_status} />
          ) : (
            <Badge tone="warn">{t('customer.primary.unverified')}</Badge>
          )}
          {p.representative_id ? (
            <Link className={`${cellLink} text-xs`} to={paths.subject(p.representative_id)}>
              {t('customer.primary.representative')}
            </Link>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">{t('customer.primary.hint')}</p>
        <ReadinessPanel subjectId={p.id} compact />
      </div>
    </Section>
  );
}

/** "Dịch vụ": the customer's services, each opening its page in Bán hàng & tài chính › Dịch vụ. */
function ServicesTab({ services }: { services: UseApiResult<{ items: Subscription[] }> }) {
  const { t, i18n } = useTranslation('accounts');
  const { t: ts } = useTranslation('subscriptions');
  const en = i18n.language.startsWith('en');
  if (services.status === 'loading') return <Loading rows={3} />;
  if (services.status === 'error') return <DataError error={services.error} onRetry={services.reload} />;
  const columns: Column<Subscription>[] = [
    {
      key: 'service',
      header: t('customer.serviceCol.service'),
      cell: (s) => (
        <Link className={cellLink} to={`/sales?tab=subscriptions&service=${encodeURIComponent(s.id)}`}>
          {s.label}
        </Link>
      ),
    },
    { key: 'plan', header: t('customer.serviceCol.plan'), cell: (s) => `${en ? s.product_name_en : s.product_name_vi} · ${en ? s.plan_name_en : s.plan_name_vi} · ${ts(`cycle.${s.cycle}`)}` },
    { key: 'state', header: t('customer.serviceCol.state'), cell: (s) => <Badge tone={STATE_TONE[s.state]} title={s.state}>{ts(`state.${s.state}`)}</Badge> },
    { key: 'due', header: t('customer.serviceCol.due'), className: 'whitespace-nowrap', cell: (s) => (s.next_due_on ? formatDate(s.next_due_on) : '—') },
    { key: 'amount', header: t('customer.serviceCol.amount'), numeric: true, cell: (s) => vnd(s.recurring_amount) },
  ];
  const items = services.data.items;
  return (
    <Section flush description={items.length >= SERVICES_SHOWN ? t('customer.servicesMore', { count: SERVICES_SHOWN }) : undefined}>
      <DataTable columns={columns} rows={items} rowKey={(s) => s.id} empty={<EmptyState title={t('customer.noServices')} />} />
    </Section>
  );
}

const CUSTOMER_TABS = ['domains', 'services', 'subjects', 'users', 'billing', 'account'] as const;
/** The customer's services listed on its page (HostBill's client profile › Services). */
const SERVICES_SHOWN = 200;
type CustomerTab = (typeof CUSTOMER_TABS)[number];

type Dialog = 'edit' | 'user' | 'register' | 'subject' | 'service';
const DIALOGS: readonly Dialog[] = ['edit', 'user', 'register', 'subject', 'service'];

/**
 * The customer page's action bar: Đăng ký tên miền, Tạo chủ thể and Sửa as buttons; adding a
 * sign-in user and every related record under "Thêm". The open dialog is in the URL
 * (`?action=register`), so Ctrl K ("Đăng ký tên miền cho <khách>") opens it directly.
 */
function CustomerActionBar({ customer: c, billing, onChanged }: { customer: CustomerOverview['customer']; billing: UseApiResult<CustomerBilling>; onChanged: () => void }) {
  const { t } = useTranslation('accounts');
  const { t: tu } = useTranslation('ux');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = params.get('action') as Dialog | null;
  const open = requested && DIALOGS.includes(requested) && ((requested !== 'register' && requested !== 'service') || c.status === 'ACTIVE') ? requested : null;
  const setOpen = (d: Dialog | null) => {
    const p = new URLSearchParams(params);
    if (d) p.set('action', d);
    else p.delete('action');
    setParams(p, { replace: true });
  };
  const control = (d: Dialog) => ({ open: open === d, onOpenChange: (o: boolean) => setOpen(o ? d : null), trigger: false });
  const q = (p: Record<string, string>) => new URLSearchParams(p).toString();
  const access = useAccess();
  const wallet = billing.status === 'success' ? (billing.data.wallets.find((w) => w.currency === c.billing_currency) ?? billing.data.wallets[0]) : undefined;
  const related = [
    { id: 'orders', to: `/sales?${q({ tab: 'orders', customer_id: c.id })}` },
    { id: 'subjects', to: `/customers?${q({ tab: 'subjects', customer_id: c.id })}` },
    ...(wallet ? [{ id: 'wallet', to: paths.ledgerAccount(wallet.id) }] : []),
    ...(access.has(['webhook.read']) ? [{ id: 'webhooks', to: `${paths.customer(c.id)}?tab=account#webhooks` }] : []),
    ...(access.has(PERMS.apiKeys) ? [{ id: 'apiKeys', to: `/settings?${q({ tab: 'security', sub: 'api-keys', owner_type: 'CUSTOMER', q: c.display_name })}` }] : []),
  ];
  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-action-bar>
        <Button type="button" size="sm" disabled={c.status !== 'ACTIVE'} onClick={() => setOpen('register')}>
          <Globe /> {tu('customer.primary.register')}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={c.status !== 'ACTIVE'} onClick={() => setOpen('service')}>
          <Server /> {tu('customer.primary.newService')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen('subject')}>
          <UserSquare2 /> {tu('customer.primary.newSubject')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen('edit')}>
          <Pencil /> {tu('customer.primary.edit')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline">
              {tu('actionBar.more')} <ChevronDown className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto">
            <DropdownMenuItem onSelect={() => setOpen('user')}>{tu('customer.more.addUser')}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">{tu('actionBar.related')}</DropdownMenuLabel>
            {related.map((r) => (
              <DropdownMenuItem key={r.id} asChild>
                <Link to={r.to}>{t(`customer.menu.${r.id}` as 'customer.menu.orders')}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <EditCustomerAction customerId={c.id} onChanged={onChanged} {...control('edit')} />
      <AddCustomerUserAction customerId={c.id} onChanged={onChanged} {...control('user')} />
      {c.status === 'ACTIVE' ? <RegisterDomainButton customer={{ id: c.id, name: c.display_name }} onChanged={onChanged} {...control('register')} /> : null}
      {c.status === 'ACTIVE' ? <NewServiceOrderButton customer={{ id: c.id, name: c.display_name }} onChanged={onChanged} {...control('service')} /> : null}
      {/* A new registrant stays on this page (its tab), so "Đăng ký tên miền" is the next click. */}
      <SubjectFormDialog
        open={open === 'subject'}
        onOpenChange={(o) => setOpen(o ? 'subject' : null)}
        customerId={c.id}
        onSaved={() => {
          void navigate(`${paths.customer(c.id)}?tab=subjects`, { replace: true });
          onChanged();
        }}
      />
    </>
  );
}

interface CustomerAttentionItem {
  tone: Tone;
  text: string;
  to: string;
  cta: string;
}

const NOT_VERIFIED = new Set(['UNVERIFIED', 'PENDING', 'FAILED', 'MISMATCH', 'EXPIRED', 'REVOKED']);
const NOT_RUNNING = new Set(['MANUAL_REVIEW', 'CREATE_FAILED', 'EXPIRED', 'GRACE_PERIOD', 'REDEMPTION', 'PENDING_DELETE']);

/** What needs doing on this account, from the overview it already loads (first page of each list). */
function customerAttention(data: CustomerOverview, billing: UseApiResult<CustomerBilling>, tu: (k: string, o?: Record<string, unknown>) => string, base: string): CustomerAttentionItem[] {
  const c = data.customer;
  const items: CustomerAttentionItem[] = [];
  if (c.status !== 'ACTIVE') items.push({ tone: 'serious', text: tu('customer.attention.suspended'), to: `${base}?action=edit`, cta: tu('customer.attention.edit') });
  const stopped = data.domains.filter((d) => NOT_RUNNING.has(d.lifecycle_state)).length;
  if (stopped) items.push({ tone: 'serious', text: tu('customer.attention.stopped', { count: stopped }), to: base, cta: tu('customer.attention.openDomains') });
  const unverified = data.subjects.filter((s) => s.status === 'ACTIVE' && NOT_VERIFIED.has(s.latest_verification_status ?? 'UNVERIFIED')).length;
  if (unverified) items.push({ tone: 'warn', text: tu('customer.attention.unverified', { count: unverified }), to: `${base}?tab=subjects`, cta: tu('customer.attention.openSubjects') });
  const dossiers = data.domains.filter((d) => d.dossier_state !== 'COMPLETE' && d.lifecycle_state !== 'DELETED').length;
  if (dossiers) items.push({ tone: 'warn', text: tu('customer.attention.dossiers', { count: dossiers }), to: base, cta: tu('customer.attention.openDomains') });
  const soon = Date.now() + 30 * 86_400_000;
  const expiring = data.domains.filter((d) => d.lifecycle_state === 'ACTIVE' && d.registry_expires_at && new Date(d.registry_expires_at).getTime() < soon).length;
  if (expiring) items.push({ tone: 'warn', text: tu('customer.attention.expiring', { count: expiring }), to: base, cta: tu('customer.attention.openDomains') });
  if (billing.status === 'success') {
    const w = billing.data.wallets.find((x) => x.currency === c.billing_currency) ?? billing.data.wallets[0];
    if (w && Number(w.available) <= 0) items.push({ tone: 'warn', text: tu('customer.attention.lowWallet'), to: `${base}?tab=billing`, cta: tu('customer.attention.openBilling') });
  }
  return items;
}

function CustomerAttention({ items }: { items: CustomerAttentionItem[] }) {
  const { t: tu } = useTranslation('ux');
  if (!items.length) return null;
  return (
    <section aria-label={tu('actionBar.attention')} className="rounded-lg border border-warning/40 bg-card" data-customer-attention>
      <div className="flex items-center gap-2 border-b px-4 py-2.5 text-sm font-medium">
        <AlertTriangle className="size-4 text-warning" aria-hidden="true" />
        {tu('actionBar.attentionCount', { count: items.length })}
      </div>
      <ul className="divide-y">
        {items.map((a) => (
          <li key={a.text} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2">
            <Badge tone={a.tone}>{tu('actionBar.fix')}</Badge>
            <span className="min-w-[12rem] flex-1 text-sm">{a.text}</span>
            <Button asChild variant="outline" size="sm">
              <Link to={a.to}>
                {a.cta} <ArrowRight />
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** `/customers/:id`: the account with its domains, registrants, users and billing. Contact data only as masked hints. */
export function CustomerPage() {
  const { t } = useTranslation('accounts');
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t: tu } = useTranslation('ux');
  const tab = (CUSTOMER_TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as CustomerTab) : 'domains';
  const result = useApi((signal) => api.getCustomerOverview(id, { signal }), [id]);
  const billing = useApi((signal) => api.getCustomerBilling(id, { signal }), [id]);
  const services = useApi((signal) => api.listSubscriptions({ customer_id: id, limit: SERVICES_SHOWN }, { signal }), [id]);
  const name = result.status === 'success' ? result.data.customer.display_name : t('customer.fallbackTitle');
  useDocumentTitle(name);
  const tiers = useTiers();
  const access = useAccess();

  const userColumns: Column<User>[] = [
    {
      key: 'name',
      header: t('customer.userCol.name'),
      cell: (u) => <EntityRef to={paths.customerUser(id, u.user_id)} id={u.user_id} label={u.display_name} sub={<Masked value={u.email_hint} />} className="min-w-40" />,
    },
    { key: 'relation', header: t('customer.userCol.relation'), cell: (u) => <EnumText group="relation" value={u.relation} /> },
    { key: 'kind', header: t('customer.userCol.kind'), cell: (u) => <EnumText group="userKind" value={u.user_kind} /> },
    { key: 'status', header: t('customer.userCol.status'), cell: (u) => <EnumText group="userStatus" value={u.status} /> },
    {
      key: 'login',
      header: t('customer.userCol.lastLogin'),
      className: 'whitespace-nowrap',
      cell: (u) => (u.last_login_at ? <span title={formatDateTime(u.last_login_at)}>{formatRelative(u.last_login_at)}</span> : <span className="text-muted-foreground">{t('common.never')}</span>),
    },
    { key: 'linked', header: t('customer.userCol.linked'), className: 'whitespace-nowrap', cell: (u) => formatDate(u.linked_at) },
  ];
  const subjectColumns: Column<Subject>[] = [
    {
      key: 'name',
      header: t('customer.subjectCol.legalName'),
      cell: (s) => (
        <EntityRef
          to={paths.subject(s.id)}
          id={s.id}
          label={s.legal_name ?? t('common.hiddenName')}
          sub={result.status === 'success' && result.data.primary_subject?.id === s.id ? t('customer.primary.badge') : undefined}
          className="min-w-40"
        />
      ),
    },
    { key: 'type', header: t('customer.subjectCol.type'), cell: (s) => <EnumText group="subjectType" value={s.subject_type} /> },
    {
      key: 'dossiers',
      header: tu('customer.dossierCol'),
      cell: (s) => {
        const mine = result.status === 'success' ? result.data.domains.filter((d) => d.subject_id === s.id) : [];
        const open = mine.filter((d) => d.dossier_state !== 'COMPLETE').length;
        if (!mine.length) return <span className="text-muted-foreground">—</span>;
        return <Badge tone={open ? 'warn' : 'ok'}>{open ? tu('customer.dossierOpen', { count: open }) : tu('customer.dossierDone')}</Badge>;
      },
    },
    {
      key: 'verification',
      header: t('customer.subjectCol.verification'),
      cell: (s) => (s.latest_verification_status ? <EnumBadge group="verification" tones={VERIFICATION} value={s.latest_verification_status} /> : <span className="text-muted-foreground">{t('common.none')}</span>),
    },
    { key: 'status', header: t('customer.subjectCol.status'), cell: (s) => <EnumBadge group="subjectStatus" tones={SUBJECT_STATUS} value={s.status} /> },
    { key: 'domains', header: t('customer.subjectCol.domains'), numeric: true, cell: (s) => formatCount(s.domain_count) },
    { key: 'country', header: t('customer.subjectCol.country'), cell: (s) => s.country_code ?? '—' },
    { key: 'created', header: t('customer.subjectCol.created'), className: 'whitespace-nowrap', cell: (s) => formatDate(s.created_at) },
  ];
  const domainColumns: Column<Domain>[] = [
    {
      key: 'domain',
      header: t('customer.domainCol.domain'),
      cell: (d) => <EntityRef to={paths.domain(d.ascii_name)} id={d.id} label={d.unicode_name} sub={d.unicode_name !== d.ascii_name ? d.ascii_name : undefined} />,
    },
    {
      key: 'registrant',
      header: t('customer.domainCol.registrant'),
      cell: (d) => (d.subject_id ? <EntityRef to={paths.subject(d.subject_id)} id={d.subject_id} label={d.subject_legal_name ?? t('common.hiddenName')} className="min-w-36" /> : <span className="text-muted-foreground">—</span>),
    },
    { key: 'registry', header: t('customer.domainCol.registry'), className: 'min-w-36 whitespace-normal', cell: (d) => <RegistryRef code={d.registry_code} name={d.registry_name} sub={`.${d.tld.replace(/^\./, '')} · ${d.registry_code}`} /> },
    { key: 'lifecycle', header: t('customer.domainCol.lifecycle'), cell: (d) => <StatusBadge map={LIFECYCLE} value={d.lifecycle_state} /> },
    { key: 'dossier', header: t('customer.domainCol.dossier'), cell: (d) => <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={d.dossier_state} /> },
    { key: 'expiry', header: t('customer.domainCol.expiry'), className: 'whitespace-nowrap', cell: (d) => formatDate(d.registry_expires_at) },
  ];

  let body;
  if (result.status === 'loading') body = <Loading label={t('customer.loading')} rows={4} />;
  else if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    body = <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('customer.notFound') : t('customer.loadError')} />;
  } else {
    const { customer: c, counts, users, subjects, domains } = result.data;
    body = (
      <Stack>
        <CustomerAttention items={customerAttention(result.data, billing, tu as unknown as (k: string, o?: Record<string, unknown>) => string, paths.customer(c.id))} />
        <StatGrid>
          <StatCard icon={Globe} label={t('customer.stat.domains')} value={formatCount(counts.domains)} hint={counts.domains > domains.length ? t('common.firstN', { count: domains.length }) : undefined} />
          <StatCard
            icon={UserRound}
            label={t('customer.stat.subjects')}
            value={formatCount(counts.subjects)}
            to={counts.subjects > subjects.length ? `/subjects?customer_id=${encodeURIComponent(c.id)}` : undefined}
            linkLabel={t('common.showAll')}
          />
          <StatCard icon={Users} label={t('customer.stat.users')} value={formatCount(counts.users)} />
          <WalletStat billing={billing} currency={c.billing_currency} />
        </StatGrid>

        <PrimarySubjectCard data={result.data} onChanged={result.reload} />

        <NavTabs
          label={name}
          active={tab}
          tabs={CUSTOMER_TABS.map((x) => ({
            id: x,
            label: x === 'services' ? t('customer.servicesTab', { count: services.status === 'success' ? services.data.items.length : 0 }) : x === 'billing' || x === 'account' ? tu(`customer.tabs.${x}`) : tu(`customer.tabs.${x}`, { count: counts[x] }),
            to: x === 'domains' ? paths.customer(c.id) : `${paths.customer(c.id)}?tab=${x}`,
          }))}
        />
        {tab === 'domains' ? (
          <Section flush description={counts.domains > domains.length ? t('common.firstN', { count: domains.length }) : undefined}>
            <DataTable columns={domainColumns} rows={domains} rowKey={(d) => d.id} empty={<EmptyState title={t('customer.noDomains')} />} />
          </Section>
        ) : null}
        {tab === 'services' ? <ServicesTab services={services} /> : null}
        {tab === 'subjects' ? (
          <Section
            flush
            actions={
              counts.subjects > subjects.length ? (
                <Link className={`${cellLink} text-sm`} to={`/subjects?customer_id=${encodeURIComponent(c.id)}`}>
                  {t('common.showAll')}
                </Link>
              ) : null
            }
          >
            <DataTable columns={subjectColumns} rows={subjects} rowKey={(s) => s.id} empty={<EmptyState title={t('customer.noSubjects')} />} />
          </Section>
        ) : null}
        {tab === 'users' ? (
          <Section flush>
            <DataTable columns={userColumns} rows={users} rowKey={(u) => u.user_id} empty={<EmptyState title={t('customer.noUsers')} />} />
          </Section>
        ) : null}
        {tab === 'billing' ? <BillingTab billing={billing} customer={c} /> : null}
        {tab === 'account' ? (
          <>
            <Section title={t('customer.account')}>
              <div className="space-y-4">
                <KeyValue
                  columns={3}
                  rows={[
                    [t('customer.field.code'), c.code ? <CopyText key="code" value={c.code} /> : '—'],
                    [t('common.status'), <EnumBadge key="s" group="customerStatus" tones={CUSTOMER_STATUS} value={c.status} />],
                    [t('common.type'), <EnumText key="t" group="accountType" value={c.account_type} />],
                    [t('customer.field.email'), <Masked key="e" value={c.email_hint} tagged />],
                    [t('customer.field.phone'), <Masked key="ph" value={c.phone_hint} tagged />],
                    [t('common.country'), c.country_code ?? '—'],
                    [t('common.tier'), <TierText key="tier" code={c.tier} tiers={tiers} />],
                    [t('customer.field.billingCurrency'), <span key="cur" className="font-mono">{c.billing_currency}</span>],
                    [t('common.created'), formatDateTime(c.created_at)],
                    [t('common.updated'), formatDateTime(c.updated_at)],
                    [t('common.id'), <CopyText key="id" value={c.id} />],
                  ]}
                />
                <Callout tone="info">{t('customer.maskedNote')}</Callout>
              </div>
            </Section>
            {c.profile ? <ProfileSection p={c.profile} /> : null}
            <ContactSharingSection customer={c} onChanged={result.reload} />
            <DisclosureSection customerId={c.id} />
            {/* Its own SMTP settings and templates only while its tier allows them (`customer.tiers.custom_email`). */}
            {tiers.find((x) => x.code === c.tier)?.custom_email && access.has(['notify.read']) ? (
              <section className="space-y-4">
                <EmailSettingsPanel owner={c.id} />
                <TemplatesPanel owner={c.id} />
              </section>
            ) : null}
            {access.has(['webhook.read']) ? (
              <section id="webhooks" className="scroll-mt-20 space-y-4">
                <WebhooksPanel owner={c.id} />
              </section>
            ) : null}
          </>
        ) : null}
      </Stack>
    );
  }

  return (
    <CustomerScope customerId={result.status === 'success' ? result.data.customer.id : null}>
      <PageHeader
        title={name}
        subtitle={
          result.status === 'success' ? (
            // The reference layouts' ID line: customer code · kind · country (the tier is the badge).
            <span className={mutedSmall}>
              {[
                result.data.customer.code ? `${t('customer.field.code')} ${result.data.customer.code}` : null,
                t(`enum.accountType.${result.data.customer.account_type}`),
                result.data.customer.country_code,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : undefined
        }
        badges={
          result.status === 'success' ? (
            <>
              <EnumBadge group="customerStatus" tones={CUSTOMER_STATUS} value={result.data.customer.status} />
              <Badge tone="info" icon={false}>
                {tu('customer.domainsBadge', { count: result.data.counts.domains })}
              </Badge>
              <Badge tone={tiers.find((x) => x.code === result.data.customer.tier)?.reseller ? 'info' : 'neutral'} icon={false}>
                <TierText code={result.data.customer.tier} tiers={tiers} />
              </Badge>
            </>
          ) : undefined
        }
        crumbs={[{ label: t('crumb') }, { label: t('customers.title'), to: '/customers' }, { label: name }]}
        actions={result.status === 'success' ? <CustomerActionBar customer={result.data.customer} billing={billing} onChanged={result.reload} /> : undefined}
      />
      {body}
    </CustomerScope>
  );
}
